import { InteractionManager } from 'react-native'
import {
  configLoggerType,
  consoleTransport,
  logger,
  type transportFunctionType,
  type defLvlType
} from 'react-native-logs'
import { DdLogs } from '@/modules/datadog'
import { useRemoteConfig } from '../../module/remote-config/useRemoteConfig'

// const datadogTransport: transportFunctionType = async (props) => {
//   // Do here whatever you want with the log message
//   // You can use any options set in config.transportOptions
//   // Eg. a console log: console.log(props.level.text, props.msg)
//   if (!props) return false

//   let logMethod = 'info'

//   if (props.options?.mapLevels && props.options.mapLevels[props.level.text]) {
//     logMethod = props.options.mapLevels[props.level.text]
//   } else {
//     logMethod = props.level.text
//   }

//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   if ((DdLogs as any)[logMethod]) {
//     // eslint-disable-next-line @typescript-eslint/no-explicit-any
//     await (DdLogs as any)[logMethod](props.msg)
//   } else {
//     await DdLogs.info(props.msg)
//   }

//   return true
// }

//#region OVERRIDES
// During dev, it's useful to override logger defaults on a file by file basis.
// Of course, we have options:
// - change per-user in Firebase Remote Config (esp for production)
// - change `loggerSeverityConfigSchema.tsx` during dev but don't commit
// - add new `"Debug": 'debug'` style loggers in config schemas and use in a file
// - or add a new `foo.bar` logger for use like this
//
// Or add an optional param to `extend("LoggerName")`. Doing that here....
// Simple runtime/build support for detecting local overrides of log levels left behind

let foundOverrides = false
function recordOverrides(context: string, level: SeverityLevels) {
  foundOverrides = true

  // TODO if console was patched, log differently, or send to DD direct!
  // TODO what level should this be? WARN?
  console.warn(`WARNING: LOCAL LOGGING OVERRIDE: "${context}" is '${level}'`)
}

// TODO this is for release checks!
export function didFindOverrides() {
  return foundOverrides
}
//#endregion OVERRIDES

export const LoggerContexts = {
  /** External */
  /** Provider-agnostic push facade (`@/modules/push`) — Customer.io. */
  Push: 'Push',
  AppVersionAlert: 'AppVersionAlert',
  AudioService: 'AudioService',

  /** General */
  Linking: 'Linking',
  NavigationUtilities: 'NavigationUtilities',
  AdminNavigator: 'AdminNavigator',
  RootNavigator: 'RootNavigator',
  StoreProvider: 'StoreProvider',

  /** Models */
  Game: 'Game',
  GameEvents: 'GameEvents',
  LegacySubscriptionClient: 'LegacySubscriptionClient',
  Messages: 'Messages',
  SessionUser: 'SessionUser',
  SessionDaily: 'SessionDaily',
  SubscriptionClient: 'SubscriptionClient',
  Membership: 'Membership',

  /** Stores */
  ChannelStore: 'ChannelStore',
  ChatStore: 'ChatStore',
  NetworkStore: 'NetworkStore',
  PermissionsStore: 'PermissionsStore',
  ShareStore: 'ShareStore',
  RootStore: 'RootStore',
  ZogoStore: 'ZogoStore',
  MondayStore: 'MondayStore',
  LessonsStore: 'LessonsStore',

  /** Components */
  AppBase: 'AppBase',
  CardAndActions: 'CardAndActions',
  DrawerNavigator: 'DrawerNavigator',
  GameModal: 'GameModal',
  PlayerCell: 'PlayerCell',
  DailyTask: 'DailyTask',
  AppleCheckout: 'AppleCheckout',

  /** Screens */
  ActiveGameScreen: 'ActiveGameScreen',
  CreateTournamentScreen: 'CreateTournamentScreen',
  GameScreen: 'GameScreen',
  LobbyScreen: 'LobbyScreen',
  SignUpScreen: 'SignUpScreen',
  ZogoScreen: 'ZogoScreen',
  JoinPremiumScreen: 'JoinPremiumScreen',
  ManageMembershipScreen: 'ManageMembershipScreen'
} as const

type SeverityLevels = defLvlType | 'log'
const levels: Record<SeverityLevels, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  log: 4
} as const

// This could come from the Zod logger schema but it seems quite awkward to Pick<> from it
type RemoteLogSeverityConfig = {
  contextSeverities: Record<string, SeverityLevels>
}

// Short sample of the remote config to test TS typing
const sample: RemoteLogSeverityConfig = {
  contextSeverities: {
    RootNavigator: 'info',
    StoreProvider: 'warn',
    AppleCheckout: 'error',
    AppVersionAlert: 'log'
  }
}

const datadogTransport: transportFunctionType = (props) => {
  if (!props.extension) {
    return
  }
  if (!isExtensionVariantEnabled(props.extension)) {
    return
  }
  switch (props.level.severity) {
    case 0:
      DdLogs.debug(props.msg)
      break
    case 1:
      DdLogs.info(props.msg)
      break
    case 2:
      DdLogs.warn(props.msg)
      break
    case 3:
      DdLogs.error(props.msg)
      break
    default:
      break
  }
}

const defaultConfig: configLoggerType = {
  levels,
  severity: 'debug',
  transport: [consoleTransport, datadogTransport],
  transportOptions: {
    colors: {
      debug: 'greenBright',
      info: 'blueBright',
      warn: 'yellowBright',
      error: 'redBright'
    }
  },
  async: true,
  // https://github.com/onubo/react-native-logs#improve-performance
  asyncFunc: InteractionManager.runAfterInteractions,
  dateFormat: 'time',
  printLevel: true,
  printDate: true,
  enabled: true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const consoleLogger: any = logger.createLogger(defaultConfig)
// @TODO: This is experimental and will need to be removed once all console.xx are replaced with extendLogger(screen|context)
if (!__DEV__) {
  consoleLogger.patchConsole()
}

export { consoleLogger as logger }

const loggerExtensionVariants: string[] = []
export const setLoggerExtensionVariants = (variants: string[]) => {
  loggerExtensionVariants.splice(0, variants.length)
  loggerExtensionVariants.push(...variants)
}
const isExtensionVariantEnabled = (extension: string) => {
  return loggerExtensionVariants.includes(extension)
}

const DEFAULT_SEVERITY = 'error'
export const extendLogger = (context: keyof typeof LoggerContexts, override?: SeverityLevels) => {
  const newLogger = logger.createLogger<keyof typeof levels>(defaultConfig)
  const extendedLogger = newLogger.extend(`[${context}]`)
  newLogger.enable()

  const severity = useRemoteConfig.getState()?.logger_severity_config?.contextSeverities?.[context]
  newLogger.setSeverity(override || severity || DEFAULT_SEVERITY)
  if (override) recordOverrides(context, override)

  useRemoteConfig?.subscribe(
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    (state: any) => state.logger_severity_config.contextSeverities[context],
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    (updatedSeverity: any) => {
      newLogger.setSeverity(updatedSeverity || DEFAULT_SEVERITY)
    }
  )
  return extendedLogger
}

export const getExtensions = () => {
  return consoleLogger.getExtensions()
}
